import { supabase } from './supabase';
import cacheService from '../services/cacheService.js';

// Helper function to format trends data for frontend
const formatTrendsData = (trends) => {
  const applicationsData = trends.map(trend => ({
    date: trend.date,
    value: trend.applications_count || 0
  }));
  
  const responsesData = trends.map(trend => ({
    date: trend.date,
    value: trend.responses_count || 0
  }));
  
  const interviewsData = trends.map(trend => ({
    date: trend.date,
    value: trend.interviews_count || 0
  }));
  
  const hiresData = trends.map(trend => ({
    date: trend.date,
    value: trend.hires_count || 0
  }));
  
  return {
    applications: applicationsData,
    responses: responsesData,
    interviews: interviewsData,
    hires: hiresData
  };
};

// Service for fetching and managing analytics data
export const analyticsService = {
  // Get overall analytics for the current user
  getUserAnalytics: async (userId) => {
    try {
      // Check Redis cache first
      const cached = await cacheService.getUserAnalytics(userId);
      if (cached) {
        if (process.env.NODE_ENV === 'development') {
          console.log('📦 User analytics from cache');
        }
        return cached;
      }

      // First try to get cached analytics data from database
      const { data: analytics, error: analyticsError } = await supabase
        .from('user_analytics')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (analyticsError && analyticsError.code !== 'PGRST116') {
        console.error('Error fetching user analytics:', analyticsError);
      }

      // If cached data exists and is recent (less than 1 hour old), use it
      if (analytics && analytics.updated_at) {
        const lastUpdate = new Date(analytics.updated_at);
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        
        if (lastUpdate > oneHourAgo) {
          const result = {
            totalApplications: analytics.total_applications || 0,
            responseRate: analytics.response_rate || 0,
            interviewRate: analytics.interview_rate || 0,
            successRate: analytics.success_rate || 0,
            averageRating: analytics.average_rating || 0,
            totalEarnings: analytics.total_earnings || 0,
            totalProfileViews: analytics.total_profile_views || 0
          };
          
          // Cache in Redis (15min TTL)
          await cacheService.setUserAnalytics(userId, result);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('💾 User analytics from DB (cached in Redis)');
          }
          
          return result;
        }
      }

      // If no cached data or it's stale, refresh it
      const { error: syncError } = await supabase.rpc('sync_user_analytics', {
        target_user_id: userId
      });

      if (syncError) {
        console.error('Error syncing user analytics:', syncError);
      }

      // Get the updated analytics
      const { data: updatedAnalytics } = await supabase
        .from('user_analytics')
        .select('*')
        .eq('user_id', userId)
        .single();

      const result = {
        totalApplications: updatedAnalytics?.total_applications || 0,
        responseRate: updatedAnalytics?.response_rate || 0,
        interviewRate: updatedAnalytics?.interview_rate || 0,
        successRate: updatedAnalytics?.success_rate || 0,
        averageRating: updatedAnalytics?.average_rating || 0,
        totalEarnings: updatedAnalytics?.total_earnings || 0,
        totalProfileViews: updatedAnalytics?.total_profile_views || 0
      };
      
      // Cache in Redis
      await cacheService.setUserAnalytics(userId, result);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('💾 User analytics from DB (cached in Redis)');
      }
      
      return result;
    } catch (error) {
      console.error('Error fetching user analytics:', error);
      // Return default values on error
      return {
        totalApplications: 0,
        responseRate: 0,
        interviewRate: 0,
        successRate: 0,
        averageRating: 0,
        totalEarnings: 0,
        totalProfileViews: 0
      };
    }
  },

  // Get application trends for a specific time range
  getApplicationTrends: async (userId, timeRange = '30d') => {
    try {
      // Check Redis cache first
      const cached = await cacheService.getApplicationTrends(userId, timeRange);
      if (cached) {
        if (process.env.NODE_ENV === 'development') {
          console.log('📦 Application trends from cache:', timeRange);
        }
        return cached;
      }

      // Try to get cached trends data first
      let daysAgo;
      
      switch (timeRange) {
        case '7d':
          daysAgo = 7;
          break;
        case '30d':
          daysAgo = 30;
          break;
        case '90d':
          daysAgo = 90;
          break;
        case '1y':
          daysAgo = 365;
          break;
        default:
          daysAgo = 30;
      }
      
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysAgo);
      
      // Get trends from the application_trends table
      const { data: trends, error } = await supabase
        .from('application_trends')
        .select('*')
        .eq('user_id', userId)
        .gte('date', startDate.toISOString().split('T')[0])
        .order('date', { ascending: true });

      if (error) {
        console.error('Error fetching application trends:', error);
      }
      
      // If no trends data, sync it first
      if (!trends || trends.length === 0) {
        await supabase.rpc('sync_application_trends', {
          target_user_id: userId
        });
        
        // Try to get trends again
        const { data: newTrends } = await supabase
          .from('application_trends')
          .select('*')
          .eq('user_id', userId)
          .gte('date', startDate.toISOString().split('T')[0])
          .order('date', { ascending: true });
          
        if (newTrends && newTrends.length > 0) {
          const result = formatTrendsData(newTrends);
          
          // Cache in Redis (15min TTL)
          await cacheService.setApplicationTrends(userId, timeRange, result);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('💾 Application trends from DB (cached in Redis)');
          }
          
          return result;
        }
      } else {
        const result = formatTrendsData(trends);
        
        // Cache in Redis
        await cacheService.setApplicationTrends(userId, timeRange, result);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('💾 Application trends from DB (cached in Redis)');
        }
        
        return result;
      }
      
      // Fallback to empty data
      return {
        applications: [],
        responses: [],
        interviews: [],
        hires: []
      };
    } catch (error) {
      console.error('Error fetching application trends:', error);
      return {
        applications: [],
        responses: [],
        interviews: [],
        hires: []
      };
    }
  },

  // Get skills analytics for the current user
  getSkillsAnalytics: async (userId) => {
    try {
      // Check Redis cache first
      const cached = await cacheService.getSkillsAnalytics(userId);
      if (cached) {
        if (process.env.NODE_ENV === 'development') {
          console.log('📦 Skills analytics from cache');
        }
        return cached;
      }

      // Get cached skills analytics
      const { data: skillsData, error } = await supabase
        .from('user_skills_analytics')
        .select('*')
        .eq('user_id', userId)
        .order('demand_percentage', { ascending: false });

      if (error) {
        console.error('Error fetching skills analytics:', error);
      }

      // If no skills data, sync it first
      if (!skillsData || skillsData.length === 0) {
        await supabase.rpc('sync_skills_analytics', {
          target_user_id: userId
        });
        
        // Try to get skills again
        const { data: newSkillsData } = await supabase
          .from('user_skills_analytics')
          .select('*')
          .eq('user_id', userId)
          .order('demand_percentage', { ascending: false });
          
        if (newSkillsData && newSkillsData.length > 0) {
          const result = newSkillsData.map(skill => ({
            name: skill.skill,
            demand: Math.round(skill.demand_percentage || 0),
            category: 'technical', // Default category
            experience: skill.user_level || 3.5,
            rating: skill.user_level || 3.5
          }));
          
          // Cache in Redis
          await cacheService.setSkillsAnalytics(userId, result);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('💾 Skills analytics from DB (cached in Redis)');
          }
          
          return result;
        }
      } else {
        const result = skillsData.map(skill => ({
          name: skill.skill,
          demand: Math.round(skill.demand_percentage || 0),
          category: 'technical', // Default category
          experience: skill.user_level || 3.5,
          rating: skill.user_level || 3.5
        }));
        
        // Cache in Redis
        await cacheService.setSkillsAnalytics(userId, result);
        
        if (process.env.NODE_ENV === 'development') {
          console.log('💾 Skills analytics from DB (cached in Redis)');
        }
        
        return result;
      }

      // Fallback: get skills from user profile if no analytics data
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('skills')
        .eq('id', userId)
        .single();

      if (profile?.skills && Array.isArray(profile.skills)) {
        const result = profile.skills.map(skill => ({
          name: typeof skill === 'string' ? skill : skill.name || 'Unknown Skill',
          demand: Math.floor(Math.random() * 50) + 25, // Random demand 25-75%
          category: 'technical',
          experience: 3.5,
          rating: 3.5
        }));
        
        // Cache with shorter TTL since it's fallback data
        await cacheService.setSkillsAnalytics(userId, result, 300); // 5min TTL
        
        return result;
      }

      return [];
    } catch (error) {
      console.error('Error fetching skills analytics:', error);
      return [];
    }
  },

  // Get recent feedback for the current user
  getRecentFeedback: async (userId) => {
    try {
      // Get feedback from the user_feedback table
      const { data: feedback, error } = await supabase
        .from('user_feedback')
        .select(`
          id,
          rating,
          comment,
          created_at,
          employer_id,
          user_profiles!user_feedback_employer_id_fkey(full_name, company_name)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) {
        console.error('Error fetching recent feedback:', error);
        return [];
      }

      return (feedback || []).map(fb => ({
        id: fb.id,
        rating: fb.rating,
        comment: fb.comment || 'No comment provided',
        date: fb.created_at,
        employer: fb.user_profiles?.full_name || fb.user_profiles?.company_name || 'Anonymous Employer'
      }));
    } catch (error) {
      console.error('Error fetching recent feedback:', error);
      return [];
    }
  },

  // Get rating breakdown for tooltip
  getRatingBreakdown: async (userId) => {
    try {
      // Get all feedback ratings for the user
      const { data: feedback, error } = await supabase
        .from('user_feedback')
        .select('rating')
        .eq('user_id', userId)
        .not('rating', 'is', null);

      if (error) {
        console.error('Error fetching rating breakdown:', error);
        return {
          totalRatings: 0,
          averageRating: 0,
          breakdown: [0, 0, 0, 0, 0],
          distribution: []
        };
      }

      if (!feedback || feedback.length === 0) {
        return {
          totalRatings: 0,
          averageRating: 0,
          breakdown: [0, 0, 0, 0, 0],
          distribution: []
        };
      }

      // Calculate breakdown
      const ratings = feedback.map(f => f.rating);
      const totalRatings = ratings.length;
      const sum = ratings.reduce((acc, rating) => acc + rating, 0);
      const averageRating = sum / totalRatings;

      // Count each star rating (1-5)
      const breakdown = [0, 0, 0, 0, 0];
      ratings.forEach(rating => {
        if (rating >= 1 && rating <= 5) {
          breakdown[rating - 1]++;
        }
      });

      // Calculate percentage distribution
      const distribution = breakdown.map(count => ({
        count,
        percentage: totalRatings > 0 ? Math.round((count / totalRatings) * 100) : 0
      }));

      return {
        totalRatings,
        averageRating: Math.round(averageRating * 10) / 10, // Round to 1 decimal
        breakdown,
        distribution
      };
    } catch (error) {
      console.error('Error fetching rating breakdown:', error);
      return {
        totalRatings: 0,
        averageRating: 0,
        breakdown: [0, 0, 0, 0, 0],
        distribution: []
      };
    }
  },

  // Get user goals
  getUserGoals: async (userId) => {
    try {
      // Try to get goals from the user_goals table if it exists
      const { data: goals, error } = await supabase
        .from('user_goals')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        // If user_goals table doesn't exist or there's an error, return empty array
        return [];
      }

      // Format the goals data
      return goals?.map(goal => ({
        id: goal.id,
        title: goal.title,
        description: goal.description,
        category: goal.category,
        targetValue: goal.target_value,
        currentValue: goal.current_value,
        unit: goal.unit,
        deadline: goal.deadline ? new Date(goal.deadline) : null,
        createdAt: new Date(goal.created_at),
        status: goal.status,
        progress: goal.target_value > 0 ? Math.round((goal.current_value / goal.target_value) * 100) : 0
      })) || [];
    } catch (error) {
      console.error('Error fetching user goals:', error);
      return [];
    }
  },
  
  // Create a new goal
  createGoal: async (userId, goalData) => {
    try {
      const { data: goal, error } = await supabase
        .from('user_goals')
        .insert({
          user_id: userId,
          title: goalData.title,
          description: goalData.description,
          category: goalData.category,
          target_value: goalData.targetValue,
          current_value: goalData.currentValue || 0,
          unit: goalData.unit,
          deadline: goalData.deadline,
          status: 'active'
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating goal:', error);
        throw error;
      }
      
      return goal;
    } catch (error) {
      console.error('Error creating goal:', error);
      throw error;
    }
  },
  
  // Update goal progress
  updateGoalProgress: async (goalId, currentValue) => {
    try {
      const { data: updatedGoal, error } = await supabase
        .from('user_goals')
        .update({ 
          current_value: currentValue,
          updated_at: new Date().toISOString()
        })
        .eq('id', goalId)
        .select()
        .single();

      if (error) {
        console.error('Error updating goal progress:', error);
        throw error;
      }
      
      return updatedGoal;
    } catch (error) {
      console.error('Error updating goal progress:', error);
      throw error;
    }
  },
  
  // Get performance insights
  getPerformanceInsights: async (userId) => {
    try {
      // Get user analytics using our fixed method
      const analytics = await this.getUserAnalytics(userId);
      
      // Get platform averages from actual data
      const { data: platformStats, error: platformError } = await supabase
        .from('job_applications')
        .select('status')
        .neq('applicant_id', userId); // Get all other users' data for comparison

      let platformAverages = {
        responseRate: 45,
        interviewRate: 32,
        averageRating: 3.8
      };

      // Calculate real platform averages if data is available
      if (!platformError && platformStats && platformStats.length > 0) {
        const totalPlatformApps = platformStats.length;
        const platformShortlisted = platformStats.filter(app => app.status === 'shortlisted').length;
        const platformInterviewed = platformStats.filter(app => app.status === 'interviewed').length;
        
        platformAverages = {
          responseRate: totalPlatformApps > 0 ? Math.round((platformShortlisted / totalPlatformApps) * 100) : 45,
          interviewRate: totalPlatformApps > 0 ? Math.round((platformInterviewed / totalPlatformApps) * 100) : 32,
          averageRating: 3.8 // Would need ratings table to calculate this
        };
      }
      
      // Generate strengths and improvements based on analytics
      const strengths = [];
      const improvements = [];
      
      // Check response rate
      if (analytics.responseRate > platformAverages.responseRate) {
        strengths.push({
          id: 1,
          title: 'High Response Rate',
          description: `Your response rate of ${analytics.responseRate}% is ${(analytics.responseRate - platformAverages.responseRate)}% above the platform average`,
          impact: 'positive',
          metric: `${analytics.responseRate}%`,
          benchmark: `${platformAverages.responseRate}%`,
          recommendation: 'Keep maintaining quick response times to employers'
        });
      } else {
        improvements.push({
          id: 1,
          title: 'Response Rate',
          description: `Your response rate of ${analytics.responseRate}% is below the platform average`,
          impact: 'negative',
          metric: `${analytics.responseRate}%`,
          benchmark: `${platformAverages.responseRate}%`,
          recommendation: 'Follow up with employers more quickly to improve response rates',
          actionItems: [
            'Set up email notifications for new messages',
            'Check the platform daily for new employer responses',
            'Respond to employer inquiries within 24 hours'
          ]
        });
      }
      
      // Check interview rate
      if (analytics.interviewRate > platformAverages.interviewRate) {
        strengths.push({
          id: 2,
          title: 'High Interview Rate',
          description: `Your interview rate of ${analytics.interviewRate}% is ${(analytics.interviewRate - platformAverages.interviewRate)}% above the platform average`,
          impact: 'positive',
          metric: `${analytics.interviewRate}%`,
          benchmark: `${platformAverages.interviewRate}%`,
          recommendation: 'Continue to highlight relevant experience in applications'
        });
      } else {
        improvements.push({
          id: 2,
          title: 'Interview Conversion Rate',
          description: `Your interview rate of ${analytics.interviewRate}% is below the platform average`,
          impact: 'negative',
          metric: `${analytics.interviewRate}%`,
          benchmark: `${platformAverages.interviewRate}%`,
          recommendation: 'Focus on improving your application quality and portfolio presentation',
          actionItems: [
            'Update your portfolio with recent work samples',
            'Customize each application to the specific job requirements',
            'Add more detailed project descriptions'
          ]
        });
      }
      
      // Check ratings
      if (analytics.averageRating > platformAverages.averageRating) {
        strengths.push({
          id: 3,
          title: 'Excellent Client Ratings',
          description: `Your average rating of ${analytics.averageRating.toFixed(1)}/5 puts you in the top tier of VAs`,
          impact: 'positive',
          metric: `${analytics.averageRating.toFixed(1)}/5`,
          benchmark: `${platformAverages.averageRating.toFixed(1)}/5`,
          recommendation: 'Continue delivering high-quality work to maintain ratings'
        });
      }
      
      // Get real opportunities based on job market data
      const { data: jobSkills, error: jobError } = await supabase
        .from('jobs')
        .select('required_skills')
        .eq('status', 'active');

      let opportunities = [];
      
      if (!jobError && jobSkills && jobSkills.length > 0) {
        // Count skill frequency in job postings
        const skillCount = {};
        jobSkills.forEach(job => {
          const skills = job.required_skills || [];
          skills.forEach(skill => {
            skillCount[skill] = (skillCount[skill] || 0) + 1;
          });
        });

        // Get user's current skills
        const { data: userProfile } = await supabase
          .from('user_profiles')
          .select('skills')
          .eq('id', userId)
          .single();

        const userSkills = userProfile?.skills || [];
        const userSkillNames = userSkills.map(skill => 
          typeof skill === 'string' ? skill : skill.name
        );

        // Find top in-demand skills that user doesn't have
        const inDemandSkills = Object.entries(skillCount)
          .filter(([skill]) => !userSkillNames.includes(skill))
          .sort(([,a], [,b]) => b - a)
          .slice(0, 3);

        opportunities = inDemandSkills.map(([skill, count], index) => ({
          id: index + 1,
          title: skill,
          description: `High-demand skill appearing in ${count} active job postings`,
          potential: count > 10 ? 'high' : count > 5 ? 'medium' : 'low',
          currentLevel: 0,
          marketDemand: Math.min(Math.round((count / jobSkills.length) * 100), 100),
          recommendation: `Consider developing ${skill} skills to increase job opportunities`,
          potentialIncrease: `+${Math.round(count / jobSkills.length * 15)}% more job matches`
        }));
      }
      
      // Get real trends data from historical applications
      const { data: historicalApps, error: histError } = await supabase
        .from('job_applications')
        .select('applied_at, status')
        .eq('applicant_id', userId)
        .gte('applied_at', new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString()) // Last 60 days
        .order('applied_at', { ascending: true });

      let trends = [];
      
      if (!histError && historicalApps && historicalApps.length > 0) {
        // Calculate current month vs previous month
        const now = new Date();
        const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const previousMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        
        const currentMonthApps = historicalApps.filter(app => 
          new Date(app.applied_at) >= currentMonthStart
        );
        const previousMonthApps = historicalApps.filter(app => 
          new Date(app.applied_at) >= previousMonthStart && 
          new Date(app.applied_at) < currentMonthStart
        );

        const currentResponses = currentMonthApps.filter(app => 
          ['shortlisted', 'interviewed', 'accepted'].includes(app.status)
        ).length;
        const previousResponses = previousMonthApps.filter(app => 
          ['shortlisted', 'interviewed', 'accepted'].includes(app.status)
        ).length;

        const currentInterviews = currentMonthApps.filter(app => 
          ['interviewed', 'accepted'].includes(app.status)
        ).length;
        const previousInterviews = previousMonthApps.filter(app => 
          ['interviewed', 'accepted'].includes(app.status)
        ).length;

        const currentHires = currentMonthApps.filter(app => app.status === 'accepted').length;
        const previousHires = previousMonthApps.filter(app => app.status === 'accepted').length;

        trends = [{
          period: 'This Month',
          applications: { 
            value: currentMonthApps.length, 
            change: currentMonthApps.length - previousMonthApps.length,
            trend: currentMonthApps.length > previousMonthApps.length ? 'up' : 
                   currentMonthApps.length < previousMonthApps.length ? 'down' : 'stable'
          },
          responses: { 
            value: currentResponses, 
            change: currentResponses - previousResponses,
            trend: currentResponses > previousResponses ? 'up' : 
                   currentResponses < previousResponses ? 'down' : 'stable'
          },
          interviews: { 
            value: currentInterviews, 
            change: currentInterviews - previousInterviews,
            trend: currentInterviews > previousInterviews ? 'up' : 
                   currentInterviews < previousInterviews ? 'down' : 'stable'
          },
          hires: { 
            value: currentHires, 
            change: currentHires - previousHires,
            trend: currentHires > previousHires ? 'up' : 
                   currentHires < previousHires ? 'down' : 'stable'
          }
        }];
      } else {
        // Fallback to basic data if no historical data
        trends = [{
          period: 'This Month',
          applications: { 
            value: analytics.totalApplications, 
            change: 0,
            trend: 'stable'
          },
          responses: { 
            value: Math.round(analytics.totalApplications * analytics.responseRate / 100), 
            change: 0,
            trend: 'stable'
          },
          interviews: { 
            value: Math.round(analytics.totalApplications * analytics.interviewRate / 100), 
            change: 0,
            trend: 'stable'
          },
          hires: { 
            value: Math.round(analytics.totalApplications * analytics.successRate / 100), 
            change: 0,
            trend: 'stable'
          }
        }];
      }
      
      return {
        strengths,
        improvements,
        opportunities,
        trends
      };
    } catch (error) {
      console.error('Error fetching performance insights:', error);
      return {
        strengths: [],
        improvements: [],
        opportunities: [],
        trends: []
      };
    }
  },
  
  // Submit user feedback
  submitFeedback: async (feedbackData) => {
    try {
      const { data: feedback, error } = await supabase
        .from('feedback')
        .insert({
          user_id: feedbackData.userId,
          employer_id: feedbackData.employerId,
          job_id: feedbackData.jobId,
          application_id: feedbackData.applicationId,
          rating: feedbackData.rating,
          comment: feedbackData.comment,
          feedback_type: feedbackData.type || 'general',
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Error submitting feedback:', error);
        throw error;
      }
      
      return feedback;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  },

  // Track user event for analytics
  trackEvent: async (userId, eventType, eventData = {}) => {
    try {
      const { data: event, error } = await supabase
        .from('analytics_events')
        .insert({
          user_id: userId,
          event_type: eventType,
          event_data: eventData,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Error tracking event:', error);
        return { success: false, error: error.message };
      }
      
      return { success: true, eventId: event.id };
    } catch (error) {
      console.error('Error tracking event:', error);
      return { success: false, error: error.message };
    }
  }
};

export default analyticsService;
